/**
 * Filter builder. A collapsible card of filter rows - each
 * a column combobox, a type-aware operator select, and a value input with
 * best-effort autocomplete. An AND/OR toggle appears once there are ≥2 rows.
 * Rows live in the `filters` atom; every edit re-estimates and re-syncs the URL.
 *
 * The match-count line and the zero-row diagnostic read the shared `lastEstimate`
 * atom (written by `state/estimate.ts`): "N rows match" plus, when an estimate
 * comes back empty with a diagnostic, a one-click fix (expand the date range to
 * the ship's coverage, or drop the strictest filter).
 */

import { html, nothing, svg, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import {
  filters,
  filterLogic,
  filterExpr,
  filterExprError,
  filterMode,
  allColumns,
  selectedShip,
  lastEstimate,
  estimatePending,
  derivedSignals,
  type FilterRow,
} from '../../state/store.ts';
import { getActiveFilters, exprKindOf } from '../../state/request.ts';
import { scheduleEstimate } from '../../state/estimate.ts';
import { syncToUrl } from '../../state/url-sync.ts';
import { setPresetActive } from '../ship/date-presets.ts';
import { startDate, endDate } from '../../state/store.ts';
import { plottableColumns, isNumericColumnName } from '../derived/derived-state.ts';
import { getColumnValues } from '../../api/endpoints.ts';
import { toDatetimeLocal } from '../../utils/dates.ts';
import { validateExpr } from './expr-parser.ts';
import type { FilterOp, ColumnInfo } from '../../api/types.ts';
import { isNarrowed, isTimeColumn, pinSlicer, sliceable, toggleSlicer } from '../plot/slicers.ts';
import { formatNum, fmtTimeShort } from '../plot/render/format.ts';
import { plotState, timeColumn } from '../../state/plot-state.ts';
import { rerenderChartFromLast } from '../plot/plot-actions.ts';
import '../../ui/column-combobox.ts';

/** Two knobs on a track: the slicer toggle. Stroke on `currentColor`. */
const SLICER_GLYPH = svg`<svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
  <path d="M2 8h12" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" />
  <rect x="3.5" y="5" width="3" height="6" rx="1.2" fill="currentColor" />
  <rect x="9.5" y="5" width="3" height="6" rx="1.2" fill="currentColor" />
</svg>`;
import '../../ui/expr-input.ts';

interface OpChoice {
  value: FilterOp;
  label: string;
}
const NUMERIC_OPS: OpChoice[] = [
  { value: '==', label: '=' },
  { value: '!=', label: '!=' },
  { value: '>', label: '>' },
  { value: '<', label: '<' },
  { value: '>=', label: '>=' },
  { value: '<=', label: '<=' },
];
const STRING_OPS: OpChoice[] = [
  { value: '==', label: '=' },
  { value: '!=', label: '!=' },
];

let nextRowId = 0;

@customElement('filter-builder')
export class FilterBuilder extends LightElement {
  @state() private collapsed = true;
  /** Autocomplete values, keyed `${ship}::${column}`. Best-effort. */
  private valueCache = new Map<string, string[]>();

  private store = new StoreController(this, [
    filters,
    filterLogic,
    filterExpr,
    filterExprError,
    filterMode,
    allColumns,
    selectedShip,
    lastEstimate,
    estimatePending,
    derivedSignals,
  ]);

  /* ── Row mutations ────────────────────────────────────── */

  private commit(): void {
    scheduleEstimate();
    syncToUrl();
  }

  /** Numbers and times compare; text only matches. The backend accepts > / < on times. */
  private opsForColumn(column: string): OpChoice[] {
    return isNumericColumnName(column) || isTimeColumn(column) ? NUMERIC_OPS : STRING_OPS;
  }

  /**
   * Add a slicer straight away: a row that is a range over a column, dragged on
   * the chart. Starts on **time** - the question a slicer answers most often is
   * "show me when" - unless time is already the X axis, where the X axis slider
   * does that job and the first plotted Y is the more useful start.
   */
  private addSlicer(): void {
    const s = plotState.value;
    const time = timeColumn();
    const names = new Set(plottableColumns().filter((c) => sliceable(c.name)).map((c) => c.name));
    const preferred = s.x_column !== time && names.has(time) ? time : [...s.y_left, ...s.y_right].find((c) => names.has(c));
    const first = preferred ? { name: preferred } : plottableColumns().find((c) => sliceable(c.name));
    const row: FilterRow = {
      id: `f${++nextRowId}`,
      column: first?.name ?? '',
      op: '>=',
      value: '',
      slicer: { min: null, max: null },
    };
    filters.set([...filters.value, row]);
    this.collapsed = false;
    this.commit();
  }

  private onToggleSlicer(row: FilterRow): void {
    toggleSlicer(row.id);
    rerenderChartFromLast();
    this.commit();
  }

  private onPinSlicer(row: FilterRow): void {
    pinSlicer(row.id);
    rerenderChartFromLast();
    this.commit();
  }

  /** "All" for an open slicer, else its range in the column's own units. */
  private sliceSummary(row: FilterRow): string {
    const r = row.slicer;
    if (!r || !isNarrowed(r)) return 'all values';
    const fmt = (v: number): string => (isTimeColumn(row.column) ? fmtTimeShort(v) : formatNum(v));
    return `${r.min != null ? fmt(r.min) : 'min'} – ${r.max != null ? fmt(r.max) : 'max'}`;
  }

  private addFilter(presetCol?: string): void {
    const initialCol = presetCol || allColumns.value[0]?.name || '';
    const row: FilterRow = {
      id: `f${++nextRowId}`,
      column: initialCol,
      op: '==',
      value: '',
    };
    filters.set([...filters.value, row]);
    this.collapsed = false;
    if (initialCol) void this.loadValues(initialCol);
    this.commit();
  }

  private removeFilter(id: string): void {
    filters.set(filters.value.filter((r) => r.id !== id));
    this.commit();
  }

  private updateColumn(id: string, name: string): void {
    filters.set(
      filters.value.map((r) => {
        if (r.id !== id) return r;
        // Keep the operator if it's still valid for the new column type, else
        // fall back to equality (e.g. switching from a numeric to a text column).
        const allowed = this.opsForColumn(name);
        const op = allowed.some((o) => o.value === r.op) ? r.op : '==';
        // Column-vs-column only applies to numeric LHS; drop it otherwise.
        const value_column = isNumericColumnName(name) ? r.value_column : undefined;
        // A slicer's range is in the old column's units - start the new one open.
        if (r.slicer) return { ...r, column: name, slicer: { min: null, max: null } };
        return { ...r, column: name, op, value_column };
      }),
    );
    void this.loadValues(name);
    this.commit();
  }

  private updateOp(id: string, op: FilterOp): void {
    filters.set(filters.value.map((r) => (r.id === id ? { ...r, op } : r)));
    this.commit();
  }

  private updateValue(id: string, value: string): void {
    filters.set(filters.value.map((r) => (r.id === id ? { ...r, value } : r)));
    this.commit();
  }

  private updateValueColumn(id: string, name: string): void {
    filters.set(filters.value.map((r) => (r.id === id ? { ...r, value_column: name } : r)));
    this.commit();
  }

  /** Columns offered as a comparison right-hand side: numeric only. */
  private rhsColumns(): ColumnInfo[] {
    return plottableColumns().filter((c) => isNumericColumnName(c.name));
  }

  /** Flip a row between comparing against a literal value and another column. */
  private toggleRhsMode(row: FilterRow): void {
    let next: Partial<FilterRow>;
    if (row.value_column) {
      next = { value_column: undefined };
    } else {
      const cols = this.rhsColumns();
      const def = cols.find((c) => c.name !== row.column)?.name ?? cols[0]?.name ?? '';
      next = { value_column: def };
    }
    filters.set(filters.value.map((r) => (r.id === row.id ? { ...r, ...next } : r)));
    this.commit();
  }

  private setLogic(logic: 'AND' | 'OR'): void {
    filterLogic.set(logic);
    this.commit();
  }

  /* ── Expression (experimental) ─────────────────────────── */

  /**
   * The expression is one row of the list, ANDed with the others. Shown while
   * it has text, or from "+ Add → Expression" until it is removed - an empty
   * expression row is a place to type, not a filter.
   */
  @state() private exprOpen = false;

  private addExpression(): void {
    this.exprOpen = true;
    this.collapsed = false;
    this.addOpen = false;
    // Focus the new input once it exists.
    void this.updateComplete.then(() => this.querySelector<HTMLTextAreaElement | HTMLInputElement>('.filter-row.is-expr textarea, .filter-row.is-expr input')?.focus());
  }

  private removeExpression(): void {
    this.exprOpen = false;
    filterExpr.set('');
    filterExprError.set(null);
    this.commit();
  }

  private onExprChange(value: string): void {
    filterMode.set('and');
    filterExpr.set(value);
    filterExprError.set(validateExpr(value, exprKindOf));
    this.commit();
  }

  /* ── Value autocomplete (best-effort) ─────────────────── */

  private async loadValues(column: string): Promise<void> {
    const ship = selectedShip.value;
    if (!ship || !column) return;
    const key = `${ship}::${column}`;
    if (this.valueCache.has(key)) return;
    try {
      const values = await getColumnValues(ship, column);
      this.valueCache.set(key, values.map((v) => String(v)));
      this.requestUpdate();
    } catch {
      // Autocomplete is best-effort - ignore failures.
    }
  }

  /* ── Zero-row diagnostic fixes ────────────────────────── */

  private fixDateCoverage(min: string, max: string): void {
    startDate.set(toDatetimeLocal(min.replace(' ', 'T')));
    endDate.set(toDatetimeLocal(max.replace(' ', 'T')));
    setPresetActive('custom');
    this.commit();
  }

  private fixRemoveStrictest(col: string, op: string, value: string): void {
    const idx = filters.value.findIndex(
      (r) => r.column === col && r.op === op && String(r.value) === value,
    );
    if (idx < 0) return;
    filters.set(filters.value.filter((_, i) => i !== idx));
    this.commit();
  }

  /* ── Rendering ────────────────────────────────────────── */

  /** The slicer switch at the end of every row - greyed for a text column. */
  private renderSlicerToggle(row: FilterRow): TemplateResult {
    const on = !!row.slicer;
    const can = sliceable(row.column);
    return html`<button
      class="slicer-toggle ${on ? 'is-on' : ''}"
      ?disabled=${!can && !on}
      aria-pressed=${on ? 'true' : 'false'}
      title=${on
        ? 'Slicer - its range is dragged on the chart, no re-query. Click to make it a normal filter'
        : can
          ? 'Make this a slicer: drag its range on the chart without re-querying'
          : 'Only number and time columns can be sliced'}
      @click=${() => this.onToggleSlicer(row)}
    >
      ${SLICER_GLYPH}
    </button>`;
  }

  /** A slicer row: the column, what its range is, and a pin to make it a filter. */
  private renderSlicerRow(row: FilterRow): TemplateResult {
    const narrowed = isNarrowed(row.slicer);
    return html`
      <div class="filter-row is-slicer">
        <div class="filter-col-picker">
          <column-combobox
            .columns=${plottableColumns().filter((c) => sliceable(c.name))}
            .value=${row.column}
            placeholder="Search columns…"
            .select=${(name: string) => this.updateColumn(row.id, name)}
          ></column-combobox>
        </div>
        <div class="filter-value-row slicer-summary">
          <span class="slicer-badge">Slicer</span>
          <span class="slicer-range ${narrowed ? 'is-narrowed' : ''}">${this.sliceSummary(row)}</span>
          ${narrowed
            ? html`<button
                class="slicer-pin"
                title="Pin: make this range a real filter, so the next Plot queries exactly this slice at full detail"
                @click=${() => this.onPinSlicer(row)}
              >
                Pin
              </button>`
            : nothing}
        </div>
        ${this.renderSlicerToggle(row)}
        <button class="remove-btn" title="Remove slicer" @click=${() => this.removeFilter(row.id)}>
          ✕
        </button>
      </div>
    `;
  }

  private renderRow(row: FilterRow): TemplateResult {
    if (row.slicer) return this.renderSlicerRow(row);
    const ops = this.opsForColumn(row.column);
    const key = selectedShip.value ? `${selectedShip.value}::${row.column}` : '';
    const values = this.valueCache.get(key) ?? [];
    const listId = `datalist-${row.id}`;
    const colMode = !!row.value_column;
    // Comparing against another column only makes sense for a numeric left side.
    const showToggle = isNumericColumnName(row.column);
    return html`
      <div class="filter-row">
        <div class="filter-col-picker">
          <column-combobox
            .columns=${plottableColumns()}
            .value=${row.column}
            placeholder="Search columns…"
            .select=${(name: string) => this.updateColumn(row.id, name)}
          ></column-combobox>
        </div>
        <div class="filter-value-row">
          <select
            class="op-select"
            .value=${row.op}
            @change=${(e: Event) =>
              this.updateOp(row.id, (e.target as HTMLSelectElement).value as FilterOp)}
          >
            ${ops.map((o) => html`<option value=${o.value}>${o.label}</option>`)}
          </select>
          ${showToggle
            ? html`<button
                class="rhs-toggle ${colMode ? 'is-col' : ''}"
                title=${colMode ? 'Comparing against a column - click for a value' : 'Comparing against a value - click for a column'}
                @click=${() => this.toggleRhsMode(row)}
              >
                ${colMode ? 'col' : '123'}
              </button>`
            : nothing}
          ${colMode
            ? html`<div class="filter-rhs-picker">
                <column-combobox
                  .columns=${this.rhsColumns()}
                  .value=${row.value_column ?? ''}
                  placeholder="column…"
                  alignRight
                  .select=${(name: string) => this.updateValueColumn(row.id, name)}
                ></column-combobox>
              </div>`
            : html`<input
                  type="text"
                  placeholder="value"
                  list=${listId}
                  .value=${typeof row.value === 'string' ? row.value : String(row.value)}
                  @change=${(e: Event) =>
                    this.updateValue(row.id, (e.target as HTMLInputElement).value)}
                />
                <datalist id=${listId}>
                  ${values.map((v) => html`<option value=${v}></option>`)}
                </datalist>`}
        </div>
        ${this.renderSlicerToggle(row)}
        <button class="remove-btn" title="Remove filter" @click=${() => this.removeFilter(row.id)}>
          ✕
        </button>
      </div>
    `;
  }

  /* ── Advanced expression rendering ────────────────────── */

  private renderExprRow(): TemplateResult {
    const help = html`
      <div class="expr-help-row">
        Combine comparisons of columns and numbers into one condition. It is
        ANDed with the other filters.
      </div>
      <div class="expr-help-row">
        <span class="expr-help-k">Operators</span>
        <code>+ - * /</code> <code>&gt; &lt; &gt;= &lt;=</code> <code>== !=</code>
        <code>AND OR NOT</code> <code>( )</code>
      </div>
      <div class="expr-help-row">
        <span class="expr-help-k">Columns</span> type the name (autocompletes), or
        wrap names with spaces in brackets: <code>[Fuel Used]</code>
      </div>
      <div class="expr-help-row">
        <span class="expr-help-k">Text</span> use single quotes: <code>status == 'running'</code>
      </div>
      <div class="expr-help-row">
        <span class="expr-help-k">Examples</span>
        <code>rpm &gt; stw * 2</code> · <code>fuel &lt; 100 AND NOT (rpm &gt;= [Max RPM])</code>
      </div>
    `;
    return html`
      <div class="filter-row is-expr">
        <div class="expr-row-body">
          <expr-input
            label="Expression"
            experimental
            placeholder="e.g. rpm > stw * 2 AND fuel < 100"
            .columns=${plottableColumns()}
            .value=${filterExpr.value}
            .validate=${(t: string) => validateExpr(t, exprKindOf)}
            .change=${(v: string) => this.onExprChange(v)}
            .help=${help}
          ></expr-input>
        </div>
        <button class="remove-btn" title="Remove expression" @click=${() => this.removeExpression()}>
          ✕
        </button>
      </div>
    `;
  }

  /* ── + Add menu ───────────────────────────────────────── */

  @state() private addOpen = false;

  private onOutsideAdd = (e: Event): void => {
    const wrap = this.querySelector('.filter-add-wrap');
    if (wrap && !e.composedPath().includes(wrap)) this.closeAdd();
  };

  private onAddKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') this.closeAdd();
  };

  private openAdd(): void {
    this.addOpen = true;
    document.addEventListener('pointerdown', this.onOutsideAdd, true);
    document.addEventListener('keydown', this.onAddKey);
  }

  private closeAdd(): void {
    this.addOpen = false;
    document.removeEventListener('pointerdown', this.onOutsideAdd, true);
    document.removeEventListener('keydown', this.onAddKey);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.closeAdd();
  }

  /**
   * One "+ Add" for the three kinds of row. A menu rather than three buttons:
   * the card stays one control wide, and each kind says in a line what it does.
   */
  private renderAdd(disabled: boolean): TemplateResult {
    const hasExpr = this.exprOpen || !!filterExpr.value.trim();
    const item = (label: string, hint: string, run: () => void, off = false) => html`<button
      class="filter-add-item"
      role="menuitem"
      ?disabled=${off}
      @click=${() => {
        this.closeAdd();
        run();
      }}
    >
      <span class="filter-add-label">${label}</span>
      <span class="filter-add-hint">${hint}</span>
    </button>`;
    return html`<span class="filter-add-wrap">
      <button
        class="btn btn-secondary filter-add-btn ${this.addOpen ? 'is-open' : ''}"
        ?disabled=${disabled}
        aria-haspopup="menu"
        aria-expanded=${this.addOpen ? 'true' : 'false'}
        @click=${() => (this.addOpen ? this.closeAdd() : this.openAdd())}
      >
        + Add <span class="filter-add-caret" aria-hidden="true"></span>
      </button>
      ${this.addOpen
        ? html`<div class="filter-add-menu" role="menu">
            ${item('Filter', 'Column, operator and value - applied in the query', () => this.addFilter())}
            ${item('Slicer', 'A range you drag on the chart - no re-query', () => this.addSlicer())}
            ${item(
              'Expression',
              hasExpr ? 'Already added - one expression holds any condition' : 'Free-form, e.g. rpm > stw * 2',
              () => this.addExpression(),
              hasExpr,
            )}
          </div>`
        : nothing}
    </span>`;
  }

  private renderMatchCount(): TemplateResult | typeof nothing {
    const active = getActiveFilters();
    if (!active.filters && !active.filter_expr) return nothing;
    if (estimatePending.value) {
      return html`<div class="filter-match-count is-pending">
        <span class="fmc-dot"></span><span>Checking…</span>
      </div>`;
    }
    const est = lastEstimate.value;
    if (!est) {
      return html`<div class="filter-match-count">
        <span class="fmc-dot"></span><span>No estimate yet</span>
      </div>`;
    }
    const n = est.row_count;
    const word = n === 1 ? 'row' : 'rows';
    return html`<div class="filter-match-count ${n === 0 ? 'is-zero' : ''}">
      <span class="fmc-dot"></span><strong>${n.toLocaleString()}</strong> ${word} match
    </div>`;
  }

  private renderDiagnostic(): TemplateResult | typeof nothing {
    const est = lastEstimate.value;
    if (!est || est.row_count !== 0 || !est.diagnostic) return nothing;
    const d = est.diagnostic;

    let body: TemplateResult;
    if (d.reason === 'date_out_of_range' && d.date_coverage) {
      const { min, max } = d.date_coverage;
      body = html`
        <div class="diag-reason">
          This ship only has data from <strong>${min}</strong> to <strong>${max}</strong>.
        </div>
        <button class="diag-fix" @click=${() => this.fixDateCoverage(min, max)}>
          Expand date range to coverage
        </button>
      `;
    } else if (d.reason === 'filters_too_strict' && d.strictest_filter) {
      const f = d.strictest_filter;
      const pct = (f.kept_pct || 0).toFixed(2);
      body = html`
        <div class="diag-reason">
          Filter <code>${f.column} ${f.op} ${String(f.value)}</code> matches only ${pct}% of rows
          in this range.
        </div>
        <button
          class="diag-fix"
          @click=${() => this.fixRemoveStrictest(f.column, f.op, String(f.value))}
        >
          Remove this filter
        </button>
      `;
    } else if (d.date_coverage) {
      const { min, max } = d.date_coverage;
      body = html`<div class="diag-reason">
        Ship coverage: <strong>${min}</strong> to <strong>${max}</strong>. Try widening the range
        or loosening filters.
      </div>`;
    } else {
      body = html`<div class="diag-reason">Try widening the date range or relaxing filters.</div>`;
    }

    return html`<div class="diagnostic-box visible">
      <div class="diag-title">No rows match</div>
      ${body}
    </div>`;
  }

  /** The structured builder view: AND/OR toggle, rows, and the add button. */
  private renderBuilder(): TemplateResult {
    const rows = filters.value;
    const count = rows.length;
    const addDisabled = !selectedShip.value || allColumns.value.length === 0;
    return html`
      ${count >= 2
        ? html`
            <div class="logic-toggle">
              <button
                class=${filterLogic.value === 'AND' ? 'active' : ''}
                @click=${() => this.setLogic('AND')}
              >
                AND
              </button>
              <button
                class=${filterLogic.value === 'OR' ? 'active' : ''}
                @click=${() => this.setLogic('OR')}
              >
                OR
              </button>
            </div>
          `
        : nothing}
      <div id="filters-container">
        ${rows.map((r) => this.renderRow(r))}
        ${this.exprOpen || filterExpr.value.trim() ? this.renderExprRow() : nothing}
      </div>
      ${filterLogic.value === 'OR' && count >= 2 && filterExpr.value.trim()
        ? html`<p class="filter-logic-note">The expression is ANDed with the OR group above.</p>`
        : nothing}
      <div class="filters-actions">
        ${this.renderAdd(addDisabled)} ${this.renderMatchCount()}
      </div>
    `;
  }

  override render(): TemplateResult {
    const count = filters.value.length + (filterExpr.value.trim() ? 1 : 0);
    return html`
      <div class="card">
        <div class="section-header" @click=${() => (this.collapsed = !this.collapsed)}>
          <span class="arrow ${this.collapsed ? '' : 'open'}">▶</span>
          <span>Filters</span>
          <span class="count">${count > 0 ? `(${count} active)` : ''}</span>
        </div>
        <div class="section-body ${this.collapsed ? 'collapsed' : ''}">
          ${this.renderBuilder()}
          ${this.renderDiagnostic()}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'filter-builder': FilterBuilder;
  }
}
